import { useEffect } from "react";
import {
  useLazyGetAccountTypeQuery,
  useLazyGetChartsAccountSelectionQuery,
  useLazyGetPaymentTermSelectionQuery,
} from "../../../../../../../store/apiSlice/accountApiSlice";
import { useLazyGetCountriesQuery } from "../../../../../../../store/apiSlice/configApiSlice";
import { useLazyGetCurrencySelectionQuery } from "../../../../../../../store/apiSlice/currencyApiSlice";
import {
  useLazyGetCustomerSegmentSelectionQuery,
  useLazyGetCustomersSelectionQuery,
  useLazyGetCustomerTagsSelectionQuery,
} from "../../../../../../../store/apiSlice/customerApiSlice";
import {
  useLazyGetBillChargeTypesSelectionQuery,
  useLazyGetBillsSelectionQuery,
  useLazyGetPrePaymentSelectionQuery,
  useLazyGetSaleInvoicesSelectionQuery,
  useLazyGetSupplierCreditNotesSelectionQuery,
} from "../../../../../../../store/apiSlice/invoiceApiSlice";
import {
  useLazyGetCoManufacturingOrderSelectionQuery,
  useLazyGetManufacturingOrderSelectionQuery,
} from "../../../../../../../store/apiSlice/manufacturingOrderApiSlice";
import {
  useLazyGetTransferOrderLineStatusQuery,
  useLazyGetTransferOrdersSelectionQuery,
  useLazyGetTransferOrderStatusQuery,
} from "../../../../../../../store/apiSlice/orderApiSlice";
import { useLazyGetPriceListsSelectionQuery } from "../../../../../../../store/apiSlice/pricingApiSlice";
import {
  useLazyGetProductAliasSelectionQuery,
  useLazyGetProductCategoriesQuery,
  useLazyGetProductCategoriesSelectionQuery,
  useLazyGetProductsSelectionQuery,
  useLazyGetProductTagsSelectionQuery,
} from "../../../../../../../store/apiSlice/productApiSlice";
import {
  useLazyGetBomsSelectionQuery,
  useLazyGetPeopleRolesQuery,
  useLazyGetPeopleSelectionQuery,
} from "../../../../../../../store/apiSlice/productionApiSlice";
import { useLazyGetPromosSelectionQuery } from "../../../../../../../store/apiSlice/promoApiSlice";
import {
  useLazyGetPurchaseOrdersSelectionQuery,
  useLazyGetPurchaseOrderStatusV2Query,
} from "../../../../../../../store/apiSlice/purchaseOrderApiSlice";
import {
  useLazyGetReceiptAdjustmentSelectionQuery,
  useLazyGetReceiptsSelectionQuery,
} from "../../../../../../../store/apiSlice/receiptApiSlice";
import { useLazyGetTransactionReasonsSelectionQuery } from "../../../../../../../store/apiSlice/reportApiSlice";
import {
  useLazyGetListOfProductsQuery,
  useLazyGetListOfSupplierQuery,
  useLazyGetListOfWarehouseQuery,
  useLazyGetUsersV2Query,
} from "../../../../../../../store/apiSlice/reportApiSliceV2";
import {
  useLazyGetQuotationSelectionQuery,
  useLazyGetReturnSelectionQuery,
  useLazyGetSalesOrderSelectionQuery,
} from "../../../../../../../store/apiSlice/salesApiSlice";
import {
  useLazyGetASNSelectionQuery,
  useLazyGetConsignmentsSelectionV2Query,
  useLazyGetConsignmentStatusQuery,
  useLazyGetDeliverySelectionQuery,
  useLazyGetEquipmentCategoriesQuery,
  useLazyGetEquipmentsSelectionQuery,
  useLazyGetEquipmentTypesSelectionQuery,
  useLazyGetShipmentLegCarriersSelectionQuery,
  useLazyGetShipmentLegDeliverTosSelectionQuery,
  useLazyGetShipmentLegDestinationsSelectionQuery,
  useLazyGetShipmentLegOriginsSelectionQuery,
  useLazyGetShipmentLegSequencesSelectionQuery,
  useLazyGetShipmentLegVehicleReferencesSelectionQuery,
  useLazyGetShipmentSelectionQuery,
} from "../../../../../../../store/apiSlice/shipmentApiSlice";
import { useLazyGetSuppliersSelectionQuery } from "../../../../../../../store/apiSlice/supplierApiSlice";
import {
  useLazyGetProductTaxClassificationSelectionQuery,
  useLazyGetTaxCategorySelectionQuery,
  useLazyGetTaxJurisdictionSelectionQuery,
  useLazyGetTaxSelectionQuery,
} from "../../../../../../../store/apiSlice/taxApiSlice";
import { useLazyGetUnitOfMeasureSelectionQuery } from "../../../../../../../store/apiSlice/unitOfMeasureApiSlice";
import {
  useLazyGetPicklistSelectionQuery,
  useLazyGetSitesSelectionQuery,
  useLazyGetStockLineStatusQuery,
  useLazyGetStockLocationsSelectionQuery,
  useLazyGetStockPackListSelectionQuery,
  useLazyGetStockReplenishmentSelectionQuery,
  useLazyGetStockZonesSelectionQuery,
  useLazyGetWarehouseStockZoneTypesQuery,
  useLazyGetWorkCentersSelectionQuery,
  useLazyGetWorkCenterTypesQuery,
} from "../../../../../../../store/apiSlice/warehouseApiSlice";

/**
 * Custom Hook: useGetDataForFilter
 *
 * Purpose:
 * Automatically triggers the appropriate API call based on the filter field (`headerName`)
 * and current `orderType`. Intended to prefetch filter dropdown values for dynamic filters
 * in a data table or report filter modal.
 *
 * Usage:
 * useGetDataForFilter({ headerName: "status", orderType: "purchase" });
 *
 * @param {Object} params
 * @param {string} params.headerName - The field label of the filter (e.g., "supplier", "status")
 * @param {string} params.orderType - The current module or order context (e.g., "shipment", "purchase")
 * @param {string} [params.allocation] - Optional context (used for certain conditions like order type in allocations)
 *
 * @returns {void}
 */

export const useGetDataForFilter = ({ headerName, orderType, allocation }) => {
  const [fetchConsignment] = useLazyGetConsignmentsSelectionV2Query();
  const [fetchSupplier] = useLazyGetListOfSupplierQuery();
  const [fetchSupplierSelection] = useLazyGetSuppliersSelectionQuery();
  const [fetchWarehouse] = useLazyGetListOfWarehouseQuery();
  const [fetchConsignmentStatus] = useLazyGetConsignmentStatusQuery();
  const [fetchPurchaseOrderStatus] = useLazyGetPurchaseOrderStatusV2Query();
  const [fetchTransferOrderStatus] = useLazyGetTransferOrderStatusQuery();
  const [fetchUsers] = useLazyGetUsersV2Query();
  const [fetchProducts] = useLazyGetListOfProductsQuery();
  const [fetchPurchaseOrders] = useLazyGetPurchaseOrdersSelectionQuery();
  const [fetchTransferOrders] = useLazyGetTransferOrdersSelectionQuery();
  const [fetchTransferOrderLineStatus] = useLazyGetTransferOrderLineStatusQuery();
  const [fetchStockLineStatus] = useLazyGetStockLineStatusQuery();
  const [fetchProductCategories] = useLazyGetProductCategoriesQuery();
  const [fetchTransactionReasons] = useLazyGetTransactionReasonsSelectionQuery();
  const [fetchReceiptsSelection] = useLazyGetReceiptsSelectionQuery();
  const [fetchProductsSelection] = useLazyGetProductsSelectionQuery();
  const [fetchProductCategoriesSelection] = useLazyGetProductCategoriesSelectionQuery();
  const [fetchProductTagssSelection] = useLazyGetProductTagsSelectionQuery();
  const [fetchProductAliasSelection] = useLazyGetProductAliasSelectionQuery();
  const [fetchCurrencySelection] = useLazyGetCurrencySelectionQuery();
  const [fetchCountries] = useLazyGetCountriesQuery();
  const [fetchStockZoneSelection] = useLazyGetStockZonesSelectionQuery();
  const [fetchStockZoneTypes] = useLazyGetWarehouseStockZoneTypesQuery();
  const [fetchStockLocationSelection] = useLazyGetStockLocationsSelectionQuery();
  const [fetchChartsAccountSelection] = useLazyGetChartsAccountSelectionQuery();
  const [fetchAccountTypeSelection] = useLazyGetAccountTypeQuery();
  const [fetchPaymentTermSelection] = useLazyGetPaymentTermSelectionQuery();
  const [fetchBillChargeTypesSelection] = useLazyGetBillChargeTypesSelectionQuery();
  const [fetchTaxRateSelection] = useLazyGetTaxSelectionQuery();
  const [fetchJurisdictionSelection] = useLazyGetTaxJurisdictionSelectionQuery();
  const [fetchBillSelection] = useLazyGetBillsSelectionQuery();
  const [fetchTaxCategorySelection] = useLazyGetTaxCategorySelectionQuery();
  const [fetchSupplierCreditNotesSelection] = useLazyGetSupplierCreditNotesSelectionQuery();
  const [fetchReceiptAdjustmentSelection] = useLazyGetReceiptAdjustmentSelectionQuery();
  const [fetchPrePaymentSelection] = useLazyGetPrePaymentSelectionQuery();
  const [fetchReplenishmentSelection] = useLazyGetStockReplenishmentSelectionQuery();
  const [fetchCustomerSelection] = useLazyGetCustomersSelectionQuery();
  const [fetchProductTaxClassificationSelection] =
    useLazyGetProductTaxClassificationSelectionQuery();
  const [fetchSiteSelection] = useLazyGetSitesSelectionQuery();
  const [fetchWorkCenterSelection] = useLazyGetWorkCentersSelectionQuery();
  const [fetchWorkCenterTypes] = useLazyGetWorkCenterTypesQuery();
  const [fetchPeopleSelection] = useLazyGetPeopleSelectionQuery();
  const [fetchBomsSelection] = useLazyGetBomsSelectionQuery();
  const [fetchPeopleRoleSelection] = useLazyGetPeopleRolesQuery();
  const [fetchCustomerTagsSelection] = useLazyGetCustomerTagsSelectionQuery();
  const [fetchCustomerSegmentsSelection] = useLazyGetCustomerSegmentSelectionQuery();
  const [fetchUnitOfMeasureSelection] = useLazyGetUnitOfMeasureSelectionQuery();
  const [fetchPricelistSelection] = useLazyGetPriceListsSelectionQuery();
  const [fetchPromoSelection] = useLazyGetPromosSelectionQuery();
  const [fetchQuotationSelection] = useLazyGetQuotationSelectionQuery();
  const [fetchSalesOrderSelection] = useLazyGetSalesOrderSelectionQuery();
  const [fetchReturnSelection] = useLazyGetReturnSelectionQuery();
  const [fetchPicklistSelection] = useLazyGetPicklistSelectionQuery();
  const [fetchStockPackList] = useLazyGetStockPackListSelectionQuery();
  const [fetchDeliverySelection] = useLazyGetDeliverySelectionQuery();
  const [fetchASNSelection] = useLazyGetASNSelectionQuery();
  const [fetchEquipmentTypesSelection] = useLazyGetEquipmentTypesSelectionQuery();
  const [fetchEquipmentsSelection] = useLazyGetEquipmentsSelectionQuery();
  const [fetchEquipmentTypeCategories] = useLazyGetEquipmentCategoriesQuery();
  const [fetchShipment] = useLazyGetShipmentSelectionQuery();
  const [fetchSaleInvoiceSelection] = useLazyGetSaleInvoicesSelectionQuery();
  const [fetchShipmentLegCarriers] = useLazyGetShipmentLegCarriersSelectionQuery();
  const [fetchShipmentLegOrigins] = useLazyGetShipmentLegOriginsSelectionQuery();
  const [fetchShipmentLegDestinations] = useLazyGetShipmentLegDestinationsSelectionQuery();
  const [fetchShipmentLegDeliverTos] = useLazyGetShipmentLegDeliverTosSelectionQuery();
  const [fetchShipmentLegVehicleReferences] =
    useLazyGetShipmentLegVehicleReferencesSelectionQuery();
  const [fetchShipmentLegSequences] = useLazyGetShipmentLegSequencesSelectionQuery();
  const [fetchManufacturingOrder] = useLazyGetManufacturingOrderSelectionQuery();
  const [fetchCoManufacturingOrder] = useLazyGetCoManufacturingOrderSelectionQuery();

  useEffect(() => {
    if (["name", "number"].includes(headerName) && orderType === "product") {
      fetchProductsSelection();
    } else if (
      (headerName === "tags" && orderType === "product") ||
      (headerName === "name" && orderType === "productTag")
    ) {
      fetchProductTagssSelection();
    } else if (
      (["name", "shipment no"].includes(headerName) &&
        ["shipment", "receiptAdjustment"].includes(orderType)) ||
      (headerName === "shipment" && orderType === "receipt")
    ) {
      fetchConsignment();
    } else if (
      headerName === "supplier" ||
      (orderType === "supplier" && ["name", "code"].includes(headerName))
    ) {
      fetchSupplier();
    } else if (headerName === "co-manf" && orderType === "coManufacturingOrder") {
      fetchSupplierSelection("is_co_manufacturer=true");
    } else if (
      [
        "facility",
        "from facility",
        "to facility",
        "default facility",
        "supply facility",
        "supplying facility",
        "co-manf whs",
        "destination whs",
      ].includes(headerName) ||
      (["name", "code"].includes(headerName) && orderType === "warehouse")
    ) {
      fetchWarehouse();
    } else if (headerName === "status" && orderType === "transfer") {
      fetchTransferOrderStatus();
    } else if (headerName === "status" && orderType === "purchase") {
      fetchPurchaseOrderStatus();
    } else if (headerName === "status" && orderType === "allocation") {
      fetchTransferOrderLineStatus();
    } else if (headerName === "status" && orderType === "inventoryDetail") {
      fetchStockLineStatus();
    } else if (headerName === "status" && orderType === "shipment") {
      fetchConsignmentStatus();
    } else if (
      ["created by", "sales rep"].includes(headerName) &&
      !(orderType === "salesRep" && headerName === "created by")
    ) {
      fetchUsers();
    } else if (
      headerName === "reason" ||
      (headerName === "code" && orderType === "transactionReason")
    ) {
      // Explicitly clear params by passing {} to prevent undefined values in the slice
      fetchTransactionReasons({});
    } else if (
      headerName === "product" ||
      headerName === "product name" ||
      headerName === "description"
    ) {
      fetchProducts();
    } else if (orderType === "shopifyCatalogue" && headerName === "pricelist") {
      fetchPricelistSelection();
    } else if (orderType === "shopifyCatalogue" && headerName === "facilities") {
      fetchWarehouse();
    } else if (orderType === "purchase" && headerName === "order name") {
      fetchPurchaseOrders(new URLSearchParams({ limit: "500" }).toString(), false);
    } else if (orderType === "purchase" && headerName === "shipments") {
      fetchConsignment("type=purchase");
    } else if (["ord no", "purchase orders", "transfer orders"].includes(headerName)) {
      const params = new URLSearchParams({ mode: "selectable" });
      if (allocation === "purchase" && headerName === "ord no") {
        params.set("allocatable", "1");
      }
      allocation === "transfer" || headerName === "transfer orders"
        ? fetchTransferOrders(params.toString())
        : fetchPurchaseOrders(params.toString());
    } else if (
      headerName === "purchase no" ||
      headerName === "transfer no" ||
      headerName === "erp supplier"
    ) {
      headerName === "transfer no" || headerName === "erp supplier"
        ? fetchTransferOrders(new URLSearchParams({ mode: "selectable" }).toString())
        : fetchPurchaseOrders();
    } else if (headerName === "category" && orderType === "equipmentType") {
      fetchEquipmentTypeCategories();
    } else if (["category", "categories"].includes(headerName)) {
      const params = new URLSearchParams({ type: "selection" }).toString();
      fetchProductCategories(params);
    } else if (
      ["document number", "receipt no", "shipment number"].includes(headerName) &&
      (orderType === "receipt" || orderType === "receiptAdjustment")
    ) {
      fetchReceiptsSelection();
    } else if (["name", "number"].includes(headerName) && orderType === "productAlias") {
      fetchProductAliasSelection();
    } else if (
      ["name", "parent category"].includes(headerName) &&
      orderType === "productCategory"
    ) {
      fetchProductCategoriesSelection();
    } else if (["buying currency", "selling currency", "currency"].includes(headerName)) {
      fetchCurrencySelection();
    } else if (headerName === "country" && orderType === "warehouse") {
      fetchCountries();
    } else if (["name", "type"].includes(headerName) && orderType === "stockZone") {
      if (headerName === "type") fetchStockZoneTypes();
      else fetchStockZoneSelection();
    } else if (
      (orderType === "chartsOfAccount" && ["name", "code"].includes(headerName)) ||
      (orderType === "prePayment" && ["account"].includes(headerName)) ||
      (orderType === "journalLine" && ["account code", "account name"].includes(headerName))
    ) {
      fetchChartsAccountSelection({});
    } else if (orderType === "chartsOfAccount" && headerName === "account type") {
      fetchAccountTypeSelection();
    } else if (orderType === "paymentTerm" && headerName === "name") {
      fetchPaymentTermSelection();
    } else if (orderType === "billChargeType" && headerName === "name") {
      fetchBillChargeTypesSelection();
    } else if (["name", "code"].includes(headerName) && orderType === "stockLocation") {
      fetchStockLocationSelection();
    } else if (headerName === "stock zone") {
      fetchStockZoneSelection();
    } else if (headerName === "location") {
      fetchStockLocationSelection();
    } else if (
      headerName === "default tax rate" ||
      (orderType === "taxRate" && ["name", "rate", "jurisdiction"].includes(headerName))
    ) {
      if (headerName === "jurisdiction") fetchJurisdictionSelection();
      else {
        fetchTaxRateSelection();
      }
    } else if (orderType === "site" && ["name", "code"].includes(headerName)) {
      fetchSiteSelection();
    } else if (orderType === "workCenter" && ["name", "code", "type"].includes(headerName)) {
      if (headerName === "type") {
        fetchWorkCenterTypes();
      } else {
        fetchWorkCenterSelection();
      }
    } else if (orderType === "people" && ["name"].includes(headerName)) {
      fetchPeopleSelection();
    } else if (orderType === "people" && ["role"].includes(headerName)) {
      fetchPeopleRoleSelection();
    } else if (orderType === "boms" && ["bom no"].includes(headerName)) {
      fetchBomsSelection({});
    } else if (["tax category name", "tax category"].includes(headerName)) {
      fetchTaxCategorySelection();
    } else if (orderType === "bill" && ["bill no", "supplier reference"].includes(headerName)) {
      fetchBillSelection();
    } else if (
      orderType === "supplierCreditNote" &&
      ["credit no", "supplier reference"].includes(headerName)
    ) {
      fetchSupplierCreditNotesSelection();
    } else if (
      orderType === "receiptAdjustment" &&
      ["receipt adjustment no"].includes(headerName)
    ) {
      fetchReceiptAdjustmentSelection();
    } else if (
      orderType === "prePayment" &&
      ["payment no", "prepayment no", "reference"].includes(headerName)
    ) {
      fetchPrePaymentSelection();
    } else if (["replenish no"].includes(headerName)) {
      fetchReplenishmentSelection();
    } else if (headerName === "product tax classification name") {
      fetchProductTaxClassificationSelection();
    } else if (["pricelist name"].includes(headerName)) {
      fetchPricelistSelection();
    } else if (
      orderType === "customer" &&
      [
        "display name",
        "type",
        "company id",
        "code",
        "status",
        "email",
        "phone",
        "sale rep",
        "sub type",
        "tags",
        "default payment term",
        "default sale account",
        "default tax rate",
      ].includes(headerName)
    ) {
      if (headerName === "tags") {
        fetchCustomerTagsSelection();
      } else if (headerName === "default payment term") {
        fetchPaymentTermSelection();
      } else if (headerName === "default sale account") {
        fetchChartsAccountSelection({ params: "type_names=Sales" });
      } else if (headerName === "default tax rate") {
        fetchTaxRateSelection();
      } else {
        fetchCustomerSelection();
      }
    } else if (orderType === "customerTag" && ["name"].includes(headerName)) {
      fetchCustomerTagsSelection();
    } else if (["customer segment name"].includes(headerName)) {
      fetchCustomerSegmentsSelection();
    } else if (orderType === "unitOfMeasure" && ["name", "code"].includes(headerName)) {
      fetchUnitOfMeasureSelection();
    } else if (
      headerName === "equipment type" ||
      (orderType === "equipmentType" && ["name", "code"].includes(headerName))
    ) {
      fetchEquipmentTypesSelection();
    } else if (orderType === "equipment" && ["number"].includes(headerName)) {
      fetchEquipmentsSelection();
    } else if (orderType === "promo" && ["title"].includes(headerName)) {
      fetchPromoSelection();
    } else if (orderType === "quote" && ["quote number"].includes(headerName)) {
      fetchQuotationSelection();
    } else if (["sales no"].includes(headerName)) {
      fetchSalesOrderSelection();
    } else if (["return no"].includes(headerName)) {
      fetchReturnSelection();
    } else if (["customer"].includes(headerName)) {
      fetchCustomerSelection();
    } else if (["picklist no"].includes(headerName)) {
      fetchPicklistSelection();
    } else if (headerName === "pack no") {
      fetchStockPackList();
    } else if (orderType === "delivery" && headerName === "delivery no") {
      fetchDeliverySelection();
    } else if (orderType === "asn" && headerName === "asn no") {
      fetchASNSelection();
    } else if (orderType === "asn" && headerName === "shipment no") {
      fetchConsignment();
    } else if (
      (orderType === "shipmentV1" && headerName === "shipment no") ||
      (orderType === "shipmentLeg" && headerName === "shipment name")
    ) {
      fetchShipment();
    } else if (orderType === "shipmentLeg") {
      if (headerName === "carrier") fetchShipmentLegCarriers();
      if (headerName === "origin") fetchShipmentLegOrigins();
      if (headerName === "destination") fetchShipmentLegDestinations();
      if (headerName === "deliver to") fetchShipmentLegDeliverTos();
      if (headerName === "vehicle / vessel") fetchShipmentLegVehicleReferences();
      if (headerName === "sequence") fetchShipmentLegSequences();
    } else if (orderType === "saleInvoice" && headerName === "invoice no") {
      fetchSaleInvoiceSelection();
    } else if (
      (orderType === "manufacturingOrder" && headerName === "mo no") ||
      (orderType === "allocation" && headerName === "manf no")
    ) {
      fetchManufacturingOrder();
    } else if (orderType === "coManufacturingOrder" && headerName === "co-manf no") {
      fetchCoManufacturingOrder();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allocation, headerName, orderType]);

  return;
};
